import { httpsCallable, getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { functions } from '@/firebase/config';
import { complianceService, ComplianceTemplate } from '@/firebase/complianceService';
import { locationService, Location } from '@/firebase/locationService';

// Track if emulator is already connected
let emulatorConnected = false;

// Helper to get functions instance (local emulator or production)
const getFunctionsInstance = () => {
  if (!functions) return functions;
  
  // Check if we're in development and should use emulator
  const isLocalhost = typeof window !== 'undefined' && (
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname.startsWith('192.168.') ||
    window.location.hostname.startsWith('10.')
  );
  
  if (isLocalhost && !emulatorConnected) {
    // Connect to emulator (only once)
    try {
      connectFunctionsEmulator(functions, 'localhost', 5001);
      emulatorConnected = true;
      console.log('✅ Connected to Firebase Functions emulator on localhost:5001');
    } catch (error: any) {
      // Already connected or error, ignore
      if (error.message?.includes('already connected')) {
        emulatorConnected = true;
      }
    }
  }
  
  return functions;
};

export interface AuditExportRequest {
  exportName?: string;
  locationIds?: string[];
  jurisdictions?: string[];
  policyIds?: string[];
  dateRange?: {
    type: 'custom' | 'last_30_days' | 'last_90_days' | 'last_12_months' | 'all_time';
    startDate?: string;
    endDate?: string;
  } | null;
  organizationId: string;
}

export interface AuditExportPreview {
  packageId: string;
  documentCount: number;
  locations: string[];
  dateRange: string;
  categories: string[];
  estimatedSize: string;
  documents: any[];
  metadata: any;
  auditTrail: any[];
  scores: any;
  sections: Array<{
    policyId: string;
    policyName: string;
    jurisdiction: string;
    category: string;
    documentCount: number;
  }>;
}

export interface AuditPackageOptions {
  includeSummary: boolean;
  includeMetadata: boolean;
  includeAuditTrail: boolean;
  includeScores: boolean;
}

/**
 * Get unique jurisdictions from all compliance templates in the organization
 */
export async function getUniqueJurisdictions(
  organizationId: string
): Promise<string[]> {
  try {
    const result = await complianceService.getAllTemplates(organizationId);
    if (!result.success || !result.data) {
      return [];
    }

    // Extract unique jurisdictions
    const jurisdictionsSet = new Set<string>();
    result.data.forEach(template => {
      if (template.jurisdiction && template.jurisdiction.trim()) {
        jurisdictionsSet.add(template.jurisdiction.trim());
      }
    });

    // Sort alphabetically
    return Array.from(jurisdictionsSet).sort();
  } catch (error) {
    console.error('Error getting unique jurisdictions:', error);
    return [];
  }
}

/**
 * Get policies filtered by jurisdiction
 */
export async function getPoliciesByJurisdiction(
  organizationId: string,
  jurisdictions: string[],
  locationIds?: string[]
): Promise<ComplianceTemplate[]> {
  try {
    // Get all templates for the organization
    const result = await complianceService.getAllTemplates(organizationId);
    if (!result.success || !result.data) {
      return [];
    }

    // Filter by jurisdiction - exact match or partial match
    const filtered = result.data.filter(template => {
      const templateJurisdiction = (template.jurisdiction || '').trim();
      if (!templateJurisdiction) return false;
      
      return jurisdictions.some(selectedJurisdiction => {
        const selected = selectedJurisdiction.trim();
        // Exact match
        if (templateJurisdiction.toLowerCase() === selected.toLowerCase()) return true;
        // Partial match (e.g., "City ABC" contains "City" or vice versa)
        const templateLower = templateJurisdiction.toLowerCase();
        const selectedLower = selected.toLowerCase();
        if (templateLower.includes(selectedLower) || selectedLower.includes(templateLower)) return true;
        return false;
      });
    });

    return filtered;
  } catch (error) {
    console.error('Error getting policies by jurisdiction:', error);
    return [];
  }
}


/**
 * Generate audit export preview
 * Calls Firebase function to generate preview
 */
export async function generateAuditExportPreview(
  request: AuditExportRequest
): Promise<AuditExportPreview> {
  try {
    const functionsInstance = getFunctionsInstance();
    const generateExport = httpsCallable(functionsInstance, 'generateAuditExport');
    const result = await generateExport(request);
    
    if (result.data && (result.data as any).success) {
      return result.data as AuditExportPreview;
    }
    
    throw new Error('Failed to generate preview');
  } catch (error) {
    console.error('Error generating audit export preview:', error);
    throw error;
  }
}

/**
 * Create audit package (generate ZIP)
 */
export async function createAuditPackage(
  packageId: string,
  request: AuditExportRequest,
  options: AuditPackageOptions
): Promise<{
  downloadUrl: string;
  fileSize: number;
  expiresAt: Date;
  documentCount: number;
  downloadedCount: number;
  downloadErrors?: string[];
}> {
  try {
    const functionsInstance = getFunctionsInstance();
    const createPackage = httpsCallable(functionsInstance, 'createAuditPackage');
    const result = await createPackage({
      packageId,
      request,
      options,
    });
    
    if (result.data && (result.data as any).success) {
      const data = result.data as any;
      
      // If it's a data URL (from emulator), create a blob URL for download
      let downloadUrl = data.downloadUrl;
      if (data.isDataUrl && data.downloadUrl.startsWith('data:')) {
        // Convert data URL to blob and create object URL
        const response = await fetch(data.downloadUrl);
        const blob = await response.blob();
        downloadUrl = URL.createObjectURL(blob);
      }
      
      return {
        downloadUrl: downloadUrl,
        fileSize: data.fileSize,
        expiresAt: new Date(data.expiresAt),
        documentCount: data.documentCount || 0,
        downloadedCount: data.downloadedCount || 0,
        downloadErrors: data.downloadErrors,
      };
    }
    
    throw new Error('Failed to create package');
  } catch (error) {
    console.error('Error creating audit package:', error);
    throw error;
  }
}

/**
 * Format date range for display
 */
function formatDateRange(dateRange?: AuditExportRequest['dateRange']): string {
  if (!dateRange) return 'All time';
  
  switch (dateRange.type) {
    case 'last_30_days':
      return 'Last 30 days';
    case 'last_90_days':
      return 'Last 90 days';
    case 'last_12_months':
      return 'Last 12 months';
    case 'all_time':
      return 'All time';
    case 'custom':
      if (dateRange.startDate && dateRange.endDate) {
        return `${dateRange.startDate} to ${dateRange.endDate}`;
      }
      return 'Custom range';
    default:
      return 'All time';
  }
}

